import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomAdminModal from "../../common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";

import NewCustomAxioActionModal from "../../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";

const modals = {
  changeConfirmation: {
    title: "Success!",
    ariaLabel: "Degree Updated Successfully Modal",
    warningText: "The degree has been successfully updated.",
    actionButtonText: "OK",
  },
  changeError: {
    title: "Error!",
    ariaLabel: "Degree Update Error Modal",
    warningText: "An error occurred while updating the degree. You may not have permission to update a degree.",
    actionButtonText: "OK",
  },
};

export enum ChangeDegreeNameModalState {
  CLOSED = "CLOSED",
  CHANGE_CONFIRMATION = "CHANGE_CONFIRMATION",
  CHANGE_ERROR = "CHANGE_ERROR",
}

const degreeNameSchema = z.object({
  degreeName: z.string().min(1, "Degree name is required"),
});

type DegreeNameFormData = z.infer<typeof degreeNameSchema>;

interface ChangeDegreeNameModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (formData: DegreeNameFormData) => Promise<any>;
  currentDegreeName: string;
}

const ChangeDegreeNameModal: React.FC<ChangeDegreeNameModalProps> = ({
  visible,
  onClose,
  onSubmit,
  currentDegreeName,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState({
    state: ChangeDegreeNameModalState.CLOSED,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
    setValue,
  } = useForm<DegreeNameFormData>({
    resolver: zodResolver(degreeNameSchema),
    defaultValues: {
      degreeName: currentDegreeName,
    },
  });

  // Reset form when currentDegreeName changes or modal becomes visible
  useEffect(() => {
    if (visible && currentDegreeName) {
      reset({ degreeName: currentDegreeName });
    }
  }, [visible, currentDegreeName, reset]);

  const handleClose = () => {
    reset();
    setModalState({ state: ChangeDegreeNameModalState.CLOSED });
    onClose();
  };

  const handleSubmitForm = async (data: DegreeNameFormData) => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(data);

      if (result?.updateDegree?.success) {
        reset();
        setModalState({ state: ChangeDegreeNameModalState.CHANGE_CONFIRMATION });
        onClose();
      } else {
        setModalState({ state: ChangeDegreeNameModalState.CHANGE_ERROR });
      }
    } catch (error) {
      setModalState({ state: ChangeDegreeNameModalState.CHANGE_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CustomAdminModal
        visible={visible}
        onRequestClose={handleClose}
        title="Change Degree Name"
        style={{ width: isMedium ? "30%" : "90%", maxHeight: "90vh" }}
        onShow={() => {}}
        ariaLabel="Change degree name modal">
        <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true} tabIndex={0}>
          <View style={styles.container}>
            <View style={styles.formGroup}>
              <CustomText
                text="Degree Name"
                size="s"
                textType="text"
                style={{ marginBottom: 4, color: "#182230" }}
                weight="ultraBold"
              />
              <Controller
                control={control}
                rules={{ required: true }}
                name="degreeName"
                render={({ field: { onChange, value } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={onChange}
                    placeholder="Bachelor of Computer Science"
                    style={styles.input}
                  />
                )}
              />
              {errors?.degreeName?.message && (
                <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.degreeName?.message} />
              )}
              <View style={{ height: 4 }} />
              <CustomText
                text="Enter a clear, descriptive name for this degree program."
                size="xs"
                style={{ color: "#6B7280" }}
              />
            </View>

            <View style={styles.buttonContainer}>
              <CustomButton
                text="Cancel"
                onPress={handleClose}
                styleType="transparent"
                size="m"
                style={styles.cancelButton}
                textStyle={styles.cancelButtonText}
              />
              <CustomButton
                text="Save Changes"
                onPress={handleSubmit(handleSubmitForm)}
                styleType="primary"
                size="m"
                style={styles.saveButton}
                textStyle={styles.saveButtonText}
                disabled={!isValid || isSubmitting}
              />
            </View>
          </View>
        </ScrollView>
      </CustomAdminModal>
      <NewCustomAxioActionModal
        visible={modalState.state === ChangeDegreeNameModalState.CHANGE_CONFIRMATION}
        onClose={() => setModalState({ state: ChangeDegreeNameModalState.CLOSED })}
        modalTexts={modals.changeConfirmation}
        contextualText={getValues("degreeName")}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === ChangeDegreeNameModalState.CHANGE_ERROR}
        onClose={() => setModalState({ state: ChangeDegreeNameModalState.CLOSED })}
        modalTexts={modals.changeError}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalScrollView: {
    flex: 1,
    width: "100%",
  },
  container: {
    padding: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  input: {
    width: "100%",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  saveButton: {
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  saveButtonText: {
    color: "#FFFFFF",
  },
});

export default ChangeDegreeNameModal;
